/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { ChatMessage } from 'types/kahu';

type ConversationMeta = {
  sessionId: string | null;
  title?: string;
};

// Maximum length for the slug portion of the filename to ensure it remains concise and readable.
const FILENAME_SLUG_MAX = 50;
const TITLE_PREVIEW_MAX = 80;

// Converts a string into a URL-friendly slug by lowercasing, replacing non-alphanumeric characters with hyphens, and trimming to a maximum length.
const slugify = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, FILENAME_SLUG_MAX);

// Renders a single message as markdown, with formatting based on the role (user, assistant, error) and including context or status information if available.
const renderMessage = (msg: ChatMessage): string => {
  if (msg.role === 'user') {
    const parts = ['**User**', '', msg.response];
    if (msg.context) {
      parts.push('', `> Context: ${msg.context}`);
    }
    return parts.join('\n');
  }

  if (msg.role === 'error') {
    return ['**Kahu** (error)', '', `> ⚠️ ${msg.response}`].join('\n');
  }

  const parts = ['**Kahu**', '', msg.response];
  const footer: string[] = [];
  if (msg.status) footer.push(msg.status);
  if (msg.duration) footer.push(`${msg.duration}ms`);
  if (footer.length) parts.push('', `*${footer.join(' • ')}*`);
  return parts.join('\n');
};

// Builds the markdown content for the conversation, including a header with metadata and the message history formatted in a readable way.
export const buildConversationMarkdown = (
  messages: ChatMessage[],
  meta: ConversationMeta
): string => {
  const clean = messages.filter((m) => m.id !== 'thinking');
  const firstUser = clean.find((m) => m.role === 'user');
  const title =
    meta.title ||
    firstUser?.response.slice(0, TITLE_PREVIEW_MAX) ||
    'Kahu Conversation';

  const metaLine: string[] = [];
  if (meta.sessionId) metaLine.push(`Session: ${meta.sessionId}`);
  metaLine.push(new Date().toISOString());
  metaLine.push(`${clean.length} messages`);

  // Markdown structure:
  const header = [
    `# ${title}`,
    '',
    `> ${metaLine.join(' • ')}`,
    '',
    '---',
    '',
  ].join('\n');

  // Each message is separated by a horizontal rule for clarity
  const body = clean.map(renderMessage).join('\n\n---\n\n');

  return `${header}${body}\n`;
};

// Generates a filename like "kahu-{slug}-{date}.md" where slug is derived from the conversation title or first user message
export const buildConversationFilename = (
  messages: ChatMessage[],
  meta: ConversationMeta
): string => {
  const firstUser = messages.find((m) => m.role === 'user');
  const seed = meta.title || firstUser?.response || meta.sessionId || 'kahu';
  const date = new Date().toISOString().slice(0, 10);
  const slug = slugify(seed) || 'kahu';
  return `kahu-${slug}-${date}.md`;
};

// Downloads the conversation as a markdown file
export const downloadConversationMarkdown = (
  messages: ChatMessage[],
  meta: ConversationMeta
): void => {
  // Filter out any 'thinking' messages which are placeholders and shouldn't be included in the export
  const clean = messages.filter((m) => m.id !== 'thinking');
  if (clean.length === 0) return;

  const md = buildConversationMarkdown(clean, meta);
  const filename = buildConversationFilename(clean, meta);

  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(url);
};
